import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import projectService from "../../services/project.service";
import CircularProgress from "../../components/CircularProgress/CircularProgress";
import Button from "react-bootstrap/Button";
import moment from "moment";
import {
  setErrorMessage,
} from "../../redux/actions/message";
import logService from "../../services/log.service";
import useMultipleSyncProgress from "../../hooks/useMultipleSyncProgress";

const DashboardLogic = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [projects, setProjects] = useState(null);
  const [totalHoursLogged, setTotalHoursLogged] = useState(null);
  const [sortLastSyncronized, setSortLastSyncronized] = useState(-1);
  const [sortBulkHours, setSortBulkHours] = useState(-1);
  // Use ref to track loading state across renders (immune to stale closures)
  const syncLoadingRowsRef = useRef(new Set());
  // Force update counter to trigger re-render when ref changes
  const [, forceUpdate] = useState(0);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [logAll, setLogAll] = useState(null);
  const [logLimit, setLogLimit] = useState(100);
  const [logCache, setLogCache] = useState(null);

  // Define reloadProjects with useCallback for stable reference
  const reloadProjects = useCallback(() => {
    projectService
      .allProjects(sortLastSyncronized, sortBulkHours)
      .then((res) => {
        setProjects(res.data.result.projects);
        setTotalHoursLogged(res.data.result.totalHoursLogged);
      })
      .catch(() => {
        dispatch(setErrorMessage("Failed to retrieve projects"));
      });
  }, [sortLastSyncronized, sortBulkHours, dispatch]);

  // Callback when sync completes - refresh data and clear loading state
  const handleSyncComplete = useCallback((boardId, boardName) => {
    // Ensure boardId is string for consistent Set comparison
    const boardIdStr = String(boardId);
    
    // Remove from ref (immune to stale closures)
    syncLoadingRowsRef.current.delete(boardIdStr);
    
    // Force re-render by updating counter
    forceUpdate(prev => prev + 1);
    
    // Reload projects data
    reloadProjects();
    // Reload log cache
    logService
      .logCache()
      .then((res) => {
        setLogCache(res.data.result);
      })
      .catch(() => {
        dispatch(setErrorMessage("Failed to retrieve cache log"));
      });
  }, [reloadProjects, dispatch]);

  // Callback when sync fails - clear loading state
  const handleSyncError = useCallback((boardId, boardName, error) => {
    // Ensure boardId is string for consistent Set comparison
    const boardIdStr = String(boardId);
    
    // Remove from ref (immune to stale closures)
    syncLoadingRowsRef.current.delete(boardIdStr);
    
    // Force re-render by updating counter
    forceUpdate(prev => prev + 1);
  }, []);

  // Multiple sync progress hook with onSyncComplete callback
  const {
    activeSyncs,
    startSync,
    cancelSync,
    retrySync,
    hideProgress,
    isWebSocketEnabled,
    webSocketConnectionState,
    retryWebSocketConnection
  } = useMultipleSyncProgress(handleSyncComplete, handleSyncError);

  // Restore loading states from activeSyncs (for reconnected syncs after page refresh)
  useEffect(() => {
    // Update loading ref based on active syncs
    activeSyncs.forEach(sync => {
      if (sync.status === 'running' || sync.status === 'syncing' || sync.status === 'waiting') {
        const boardIdStr = String(sync.boardId);
        if (!syncLoadingRowsRef.current.has(boardIdStr)) {
          syncLoadingRowsRef.current.add(boardIdStr);
        }
      }
    });
    
    // Force re-render to show loading spinners
    if (activeSyncs.length > 0) {
      forceUpdate(prev => prev + 1);
    }
  }, [activeSyncs]);

  // Warn user before leaving page if there are active syncs
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const activeSyncCount = activeSyncs.filter(sync => 
        sync.status === 'running' || sync.status === 'syncing' || sync.status === 'waiting'
      ).length;
      
      if (activeSyncCount > 0) {
        e.preventDefault();
        e.returnValue = `You have ${activeSyncCount} active sync${activeSyncCount > 1 ? 's' : ''} running. They will continue in the background and you can check the progress after refreshing.`;
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [activeSyncs]);

  const tableColumns = [
    {
      Header: "Board Name",
      accessor: "boardName", // accessor is the "key" in the data
    },
    {
      Header: "Board Id",
      accessor: "boardId",
    },
    {
      Header: "Last Tracked",
      accessor: "lastTracked",
      sortType: (rowA, rowB, columnId) => {
        const lastTrackedA = moment(
          rowA.values[columnId],
          "DD MMMM YYYY, HH:mm"
        );
        const lastTrackedB = moment(
          rowB.values[columnId],
          "DD MMMM YYYY, HH:mm"
        );
        return lastTrackedA - lastTrackedB;
      },
    },
    {
      Header: "Last Syncronized",
      accessor: "lastSynchronized",
      // sortType: (rowA, rowB, columnId) => {
      //   const months = {
      //     "January": '01',
      //     "February": '02',
      //     "March": '03',
      //     "April": '04',
      //     "May": '05',
      //     "June": '06',
      //     "July": '07',
      //     "August": '08',
      //     "September": '09',
      //     "October": '10',
      //     "November": '11',
      //     "December": '12'
      //   };
      //   const [dayA, monthA, yearA, timeA] = rowA.values[columnId].split(/[\s,]+/);
      //   const [dayB, monthB, yearB, timeB] = rowB.values[columnId].split(/[\s,]+/);
      //   const dateA =new Date(`${yearA}-${months[monthA]}-${dayA}T${timeA}:00.000Z`);
      //   const dateB = new Date(`${yearB}-${months[monthB]}-${dayB}T${timeB}:00.000Z`);
      //   return dateA.getTime() - dateB.getTime();
      // },

      sortType: (rowA, rowB, columnId) => {
        const dateA = moment(rowA.values[columnId], "DD MMMM YYYY, HH:mm");
        const dateB = moment(rowB.values[columnId], "DD MMMM YYYY, HH:mm");
        return dateA - dateB;
      },
    },
    {
      Header: "Time Logged",
      accessor: "totalLogged",
      sortType: (rowA, rowB, columnId) => {
        // Mengambil nilai waktu dari kolom
        const timeA = rowA.values[columnId];
        const timeB = rowB.values[columnId];

        // Fungsi untuk mengonversi nilai waktu menjadi jumlah menit
        const convertToMinutes = (time) => {
          const [hours, minutes] = time.split(" ");
          let totalMinutes = 0;
          if (hours.includes("h")) {
            totalMinutes += parseInt(hours.replace("h", ""), 10) * 60;
          }
          if (minutes.includes("m")) {
            totalMinutes += parseInt(minutes.replace("m", ""), 10);
          }
          return totalMinutes;
        };

        // Mengonversi nilai waktu menjadi jumlah menit
        const minutesA = convertToMinutes(timeA);
        const minutesB = convertToMinutes(timeB);

        // Membandingkan jumlah menit untuk sorting
        return minutesA - minutesB;
      },
    },
    {
      Header: "Hours Remaining",
      accessor: "bulkHoursLeft",
      sortType: (rowA, rowB, columnId) => {
        // Mengambil nilai waktu dari kolom
        const timeA = rowA.values[columnId];
        const timeB = rowB.values[columnId];

        // Fungsi untuk mengonversi nilai waktu menjadi jumlah menit
        const convertToMinutes = (time) => {
          const [hours, minutes] = time.split(" ");
          let totalMinutes = 0;
          if (hours.includes("h")) {
            totalMinutes += parseInt(hours.replace("h", ""), 10) * 60;
          }
          if (minutes.includes("m")) {
            totalMinutes += parseInt(minutes.replace("m", ""), 10);
          }
          return totalMinutes;
        };

        // Mengonversi nilai waktu menjadi jumlah menit
        const minutesA = convertToMinutes(timeA);
        const minutesB = convertToMinutes(timeB);

        // Membandingkan jumlah menit untuk sorting
        return minutesA - minutesB;
      },
    },
    {
      Header: "Status",
      accessor: "isUpToDate",
      Cell: ({ cell: { value, row } }) => {
        // Use ref directly to avoid stale state from re-renders
        const isLoading = syncLoadingRowsRef.current.has(String(row.original.boardId));
        
        // Find active sync for this board to get progress
        const activeSync = activeSyncs.find(sync => String(sync.boardId) === String(row.original.boardId));
        const progress = activeSync?.progress || 0;
        const syncMessage = activeSync?.message || 'Syncing...';
        
        return isLoading ? (
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <CircularProgress 
              progress={progress} 
              size={32} 
              strokeWidth={3}
              message={syncMessage}
            />
          </div>
        ) : value ? (
          "Up to date"
        ) : (
          <Button
            variant="link"
            className="text-danger"
            onClick={(e) => {
              e.preventDefault();
              syncProject(row.original.boardId);
            }}
            style={{ padding: 0 }}
          >
            Sync
          </Button>
        );
      },
      sortType: (rowA, rowB, columnId) => {
        const statusA = rowA.values[columnId] ? 1 : 0;
        const statusB = rowB.values[columnId] ? 1 : 0;
        return statusA - statusB;
      },
    },
    {
      id: "boardUrl",
      Header: "Board URL",
      accessor: "boardUrl",
      disableGlobalFilter: true,
    },
  ];

  const hiddenColumns = ["boardUrl"];

  const tableActionButtons = [
    {
      name: "See Detail",
      variant: "blue",
      action: (row) => navigate("/project-detail/" + row.boardId),
    },
    {
      name: "Open Board",
      variant: "darkblue",
      action: (row) => window.open(row.boardUrl),
    },
  ];

  useEffect(() => {
    setProjects(null);
    projectService
      .allProjects(sortLastSyncronized, sortBulkHours)
      .then((res) => {
        setProjects(res.data.result.projects);
        setTotalHoursLogged(res.data.result.totalHoursLogged);
      })
      .catch(() => {
        dispatch(setErrorMessage("Failed to retrieve projects"));
      });
    return () => {};
  }, [sortLastSyncronized, sortBulkHours, dispatch]);

  useEffect(() => {
    setLogCache(null);
    logService
      .logCache()
      .then((res) => {
        setLogCache(res.data.result);
      })
      .catch(() => {
        dispatch(setErrorMessage("Failed to retrieve cache log"));
      });
    return () => {};
  }, [dispatch]);

  const syncProject = async (boardId) => {
    // Ensure boardId is string for consistent Set comparison
    const boardIdStr = String(boardId);
    
    // Find project name for better UX
    const project = projects?.find(p => String(p.boardId) === boardIdStr);
    const boardName = project?.boardName || `Project ${boardIdStr}`;

    try {
      // Add to ref (immune to stale closures)
      syncLoadingRowsRef.current.add(boardIdStr);
      
      // Force re-render by updating counter
      forceUpdate(prev => prev + 1);
      
      // Start sync (WebSocket will handle progress and completion)
      await startSync(boardIdStr, boardName);
      
      // Note: Loading spinner will be cleared by handleSyncComplete callback
      // when sync actually completes (not here in finally block)
    } catch (error) {
      console.error('Sync initiation failed:', error);
      
      // Remove from ref if sync initiation fails
      syncLoadingRowsRef.current.delete(boardIdStr);
      
      // Force re-render by updating counter
      forceUpdate(prev => prev + 1);
      
      // Error handling is done in the hook
    }
  };

  const loadAllLog = useCallback(() => {
    logService
      .logAll(logLimit)
      .then((res) => {
        setLogAll(res.data.data);
      })
      .catch(() => {
        dispatch(setErrorMessage("Failed to load the log detail"));
      });
  }, [logLimit, dispatch]);

  useEffect(() => {
    loadAllLog();
    return () => {};
  }, [loadAllLog]);

  // Function to format date
  const formatDateTime = (isoString) => {
    const date = new Date(isoString);

    // Format date as DD-MM-YYYY
    const formattedDate = date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "UTC",
      })
      .replace(/\//g, "-");

    // Format time as HH:MM AM/PM
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC",
    });

    return {
      date: formattedDate,
      time: formattedTime + " UTC",
    };
  };

  return {
    formatDateTime,
    tableColumns,
    tableActionButtons,
    hiddenColumns,
    projects,
    totalHoursLogged,
    sortLastSyncronized,
    sortBulkHours,
    syncLoadingRowsRef,
    isHistoryOpen,
    setIsHistoryOpen,
    navigate,
    setSortLastSyncronized,
    setSortBulkHours,
    logCache,
    logAll,
    logLimit,
    setLogLimit,
    // Multiple sync progress
    activeSyncs,
    cancelSync,
    retrySync,
    hideProgress,
    // WebSocket status
    isWebSocketEnabled,
    webSocketConnectionState,
    retryWebSocketConnection,
  };
};

export default DashboardLogic;
